import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import Icons from '@aurora/shared-client/icons';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useContext } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { useUIDSeed } from 'react-uid';
import useTranslation from '../../useTranslation';
import localStyles from './CookieBannerFloatingBox.module.pcss';
import type { GdprCookies } from '@aurora/shared-client/helpers/cookie/CookieHelper';
import {
  fetchGdprCookies,
  GdprCookiePreferences,
  GdprCookieValues,
  setGdprCookies
} from '@aurora/shared-client/helpers/cookie/CookieHelper';
import { COOKIE_BANNER_FLOATING_BOX_ID } from '../CookieBanner/CookieBanner';
import useFloatingBox from '@aurora/shared-client/components/context/FloatingBoxContext/useFloatingBox';

interface CookieBannerInternalProps {
  /**
   * Handler for when the preferences modal is toggled.
   *
   * @param show whether the modal should be shown or hidden
   */
  onShowModal: (show: boolean) => void;
  /**
   * URL to the cookie privacy policy.
   */
  cookiePrivacyPolicyUrl?: string;
}

/**
 * Cookie Banner floating box component.
 *
 * @author Rishabh Jivan, Willi Hyde
 */
const CookieBannerFloatingBox: React.FC<React.PropsWithChildren<CookieBannerInternalProps>> = ({
  onShowModal,
  cookiePrivacyPolicyUrl
}) => {
  const cx = useClassNameMapper(localStyles);
  const {
    formatMessage,
    loading: textLoading,
    FormattedMessage
  } = useTranslation(EndUserComponent.COOKIE_BANNER_FLOATING_BOX);
  const {
    publicConfig: { trafficVisitorCookieMaxAge }
  } = useContext(TenantContext);
  const gdprCookies: GdprCookies = fetchGdprCookies();
  const uidSeed = useUIDSeed();
  const ariaTitleId = uidSeed('alertTitle');
  const ariaBodyId = uidSeed('alertBody');
  const { removeBox } = useFloatingBox(COOKIE_BANNER_FLOATING_BOX_ID);

  /**
   * Handler for when the Accept button is clicked.
   * This will set all the cookie categories to accepted.
   */
  const onAccept = () => {
    const cookiePrefs: GdprCookiePreferences = new GdprCookiePreferences();
    for (const key of Object.keys(gdprCookies.preferences)) {
      cookiePrefs[key] = GdprCookieValues.ACCEPTED;
    }
    setGdprCookies(cookiePrefs, trafficVisitorCookieMaxAge);
    removeBox();
  };

  /**
   * Handler for when the Decline button is clicked.
   * This will set only the necessary cookies to accepted and the rest are rejected.
   */
  const onDecline = () => {
    const cookiePreferences: GdprCookiePreferences = new GdprCookiePreferences();
    for (const key of Object.keys(gdprCookies.preferences)) {
      cookiePreferences[key] = GdprCookieValues.REJECTED;
    }
    cookiePreferences.LithiumNecessaryCookiesAccepted = GdprCookieValues.ACCEPTED;
    setGdprCookies(cookiePreferences, trafficVisitorCookieMaxAge);
    removeBox();
  };

  if (textLoading) {
    return null;
  }

  return (
    <div
      className={cx('lia-banner')}
      role="alertdialog"
      aria-labelledby={ariaTitleId}
      aria-describedby={ariaBodyId}
      data-testid="CookieBanner"
    >
      <span className={cx('lia-title')} id={ariaTitleId}>
        {formatMessage('bannerTitle')}
      </span>
      <span className={cx('lia-message')} id={ariaBodyId}>
        <FormattedMessage
          id="bannerText"
          values={{
            Link: function renderChunks(chunks): React.ReactNode {
              return cookiePrivacyPolicyUrl ? (
                <a href={cookiePrivacyPolicyUrl} target="_blank" rel="noreferrer">
                  {chunks}
                  <Icon
                    icon={Icons.ExternalLinkIcon}
                    className={cx('lia-link-icon')}
                    size={IconSize.PX_14}
                    color={IconColor.LINK_COLOR}
                  />
                </a>
              ) : (
                <>{chunks}</>
              );
            }
          }}
        />
      </span>
      <div className={cx('lia-actions')}>
        <Button
          variant={ButtonVariant.SECONDARY}
          onClick={() => onShowModal(true)}
          data-testid="CookieBanner.PreferencesButton"
        >
          {formatMessage('preferences')}
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={onDecline}
          data-testid="CookieBanner.DeclineButton"
        >
          {formatMessage('decline')}
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={onAccept}
          data-testid="CookieBanner.AcceptButton"
        >
          {formatMessage('accept')}
        </Button>
      </div>
    </div>
  );
};

export default CookieBannerFloatingBox;
